
import React, { useState } from 'react';
import { ChevronDown, MessageCircle, HelpCircle } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import FadeIn from './FadeIn';
import SEO from './SEO';

const FAQ: React.FC = () => {
  const { t, language } = useLanguage();
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggleIndex = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // Build FAQ Schema
  const faqSchema = {
    "@type": "FAQPage",
    "mainEntity": t.faq.items.map(item => ({
      "@type": "Question",
      "name": item.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.a
      }
    }))
  };

  return (
    <section className="min-h-screen bg-[#120C08] pt-32 pb-24 relative overflow-hidden">
        <SEO 
          title={language === 'he' ? 'שאלות ותשובות' : 'FAQ'} 
          description={t.faq.subtitle}
          path="/faq"
          schema={faqSchema}
        />
        
        {/* Background Elements */}
        <div className="absolute top-20 right-0 w-96 h-96 bg-fait-gold/5 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-20 left-0 w-64 h-64 bg-fait-brown/10 rounded-full blur-3xl pointer-events-none"></div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            
            {/* Header */}
            <div className="text-center mb-16">
                <FadeIn direction="down">
                    <div className="flex justify-center mb-4">
                        <HelpCircle className="w-12 h-12 text-fait-gold opacity-80" />
                    </div>
                    <span className="text-fait-gold font-sans text-sm tracking-widest uppercase mb-2 block">{t.faq.title}</span>
                    <h2 className="text-4xl md:text-5xl font-serif font-bold text-white drop-shadow-md">{t.faq.subtitle}</h2>
                </FadeIn>
            </div>

            {/* FAQ Items */}
            <div className="space-y-4">
                {t.faq.items.map((item, index) => (
                    <FadeIn key={index} delay={index * 100} className="w-full">
                        <div 
                            className={`border transition-all duration-300 rounded-xl overflow-hidden ${
                                openIndex === index 
                                    ? 'bg-[#1E1611] border-fait-gold/30 shadow-[0_0_15px_rgba(255,179,0,0.05)]' 
                                    : 'bg-[#1a120b] border-white/5 hover:border-white/10'
                            }`}
                        >
                            <button 
                                onClick={() => toggleIndex(index)}
                                className="w-full px-6 py-5 flex items-center justify-between text-right rtl:text-right ltr:text-left focus:outline-none"
                            >
                                <div className="flex items-center gap-4">
                                    <MessageCircle size={20} className={`shrink-0 ${openIndex === index ? 'text-fait-gold' : 'text-gray-500'}`} />
                                    <span className={`text-lg font-serif font-medium ${openIndex === index ? 'text-white' : 'text-gray-300'}`}>
                                        {item.q}
                                    </span>
                                </div>
                                <ChevronDown 
                                    className={`w-5 h-5 text-gray-500 transition-transform duration-300 ${openIndex === index ? 'rotate-180 text-fait-gold' : ''}`} 
                                />
                            </button>
                            
                            <div 
                                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                                    openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                                }`}
                            >
                                <div className="px-6 pb-6 pt-0 pl-14 rtl:pl-6 rtl:pr-14">
                                    <p className="text-gray-400 font-sans leading-relaxed border-t border-white/5 pt-4">
                                        {item.a}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </FadeIn>
                ))}
            </div>

        </div>
    </section>
  );
};

export default FAQ;
